<template>
  <div class="divide-gray-lighten-2 divide-y" v-if="modelValue">
    <PContextMenuItem :disabled="!selectedText" @click.stop="handleCut">
      <FontAwesomeIcon :icon="['fas', 'scissors']" />
      <span>Knippen</span>
    </PContextMenuItem>
    <PContextMenuItem :disabled="!selectedText" @click.stop="handleCopy">
      <FontAwesomeIcon :icon="['fas', 'copy']" />
      <span>Kopiëren</span>
    </PContextMenuItem>
    <PContextMenuItem @click.stop="handlePaste">
      <FontAwesomeIcon :icon="['fas', 'paste']" />
      <span>Plakken</span>
    </PContextMenuItem>
  </div>
</template>

<script setup lang="ts">
import PContextMenuItem from "@/components/overlay/PContextMenuItem.vue"
import { onMounted } from "vue"

const emit = defineEmits<{ closeMenu: [] }>()

const modelValue = defineModel<HTMLInputElement | HTMLTextAreaElement | HTMLElement>({
  required: true
})

const target = modelValue.value
const textControl =
  target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement ? target : null
const start = textControl?.selectionStart ?? null
const end = textControl?.selectionEnd ?? null
let contentEditableRange: Range | null = null
let selectedText = ""

if (textControl) {
  selectedText = textControl.value.substring(start ?? 0, end ?? 0)
} else {
  const selection = window.getSelection()
  const range = selection?.rangeCount ? selection.getRangeAt(0) : null
  if (range && target.contains(range.commonAncestorContainer)) {
    contentEditableRange = range.cloneRange()
    selectedText = range.toString()
  }
}

async function writeClipboardText(text: string) {
  try {
    await navigator.clipboard.writeText(text)
  } catch (error) {
    console.error(
      "Failed to write to clipboard:",
      error instanceof Error ? error.message : "Unknown error"
    )
  }
}

async function handleCut() {
  await writeClipboardText(selectedText)
  if (textControl) {
    textControl.setRangeText("", start ?? 0, end ?? 0, "end")
  } else if (contentEditableRange) {
    restoreContentEditableSelection()
    contentEditableRange.deleteContents()
  }
  dispatchInput()
  closeMenu()
}

async function handleCopy() {
  await writeClipboardText(selectedText)
  closeMenu()
}

async function handlePaste() {
  try {
    const text = await navigator.clipboard.readText()
    if (textControl) {
      textControl.setRangeText(text, start ?? 0, end ?? 0, "end")
    } else {
      const range = contentEditableRange ?? createRangeAtEnd()
      restoreContentEditableSelection(range)
      range.deleteContents()

      const textNode = document.createTextNode(text)
      range.insertNode(textNode)
      range.setStartAfter(textNode)
      range.collapse(true)
      contentEditableRange = range
      restoreContentEditableSelection(range)
    }
    dispatchInput()
  } catch (error) {
    console.error(
      "Failed to read from clipboard:",
      error instanceof Error ? error.message : "Unknown error"
    )
  }
  closeMenu()
}

function createRangeAtEnd() {
  const range = document.createRange()
  range.selectNodeContents(target)
  range.collapse(false)
  return range
}

function restoreContentEditableSelection(range = contentEditableRange) {
  if (textControl || !range) return

  target.focus()
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
}

function dispatchInput() {
  target.dispatchEvent(new Event("input", { bubbles: true }))
}

function closeMenu() {
  emit("closeMenu")
}

onMounted(() => {
  target.focus()
  restoreContentEditableSelection()
})
</script>

<style scoped></style>
